// Tiny head/meta/JSON-LD manager without Helmet.
// Ensures idempotent tags via data-key, and removes on unmount.

type Cleanup = () => void;

function ensureTag<K extends HTMLElement>(
  tagName: string,
  attrs: Record<string, string>,
  key?: string
): K {
  let el = key
    ? (document.head.querySelector(`[data-key="${key}"]`) as K | null)
    : null;
  if (!el) {
    el = document.createElement(tagName) as K;
    if (key) el.setAttribute("data-key", key);
    document.head.appendChild(el);
  }
  for (const [k, v] of Object.entries(attrs)) {
    if (k === "textContent") {
      (el as any).textContent = v;
    } else {
      el.setAttribute(k, v);
    }
  }
  return el;
}

export function setTitle(title: string, key = "title"): Cleanup {
  const prev = document.title;
  document.title = title;
  return () => { document.title = prev; };
}

export function setLinkCanonical(href: string, key = "canonical"): Cleanup {
  const el = ensureTag<HTMLLinkElement>("link", { rel: "canonical", href }, key);
  return () => { el.parentNode?.removeChild(el); };
}

export function setMetaName(name: string, content: string, key?: string): Cleanup {
  const k = key ?? `meta-name-${name}`;
  const el = ensureTag<HTMLMetaElement>("meta", { name, content }, k);
  return () => { el.parentNode?.removeChild(el); };
}

export function setMetaProperty(property: string, content: string, key?: string): Cleanup {
  const k = key ?? `meta-prop-${property}`;
  const el = ensureTag<HTMLMetaElement>("meta", { property, content }, k);
  return () => { el.parentNode?.removeChild(el); };
}

export function injectJsonLd(obj: unknown, key: string): Cleanup {
  const el = ensureTag<HTMLScriptElement>("script", {
    type: "application/ld+json",
    textContent: JSON.stringify(obj)
  }, `jsonld-${key}`);
  return () => { el.parentNode?.removeChild(el); };
} 